import { useCallback, useEffect, useMemo, useState } from 'react';
import { ValidationError } from 'yup';
import { Button } from '@cbhq/cds-web/buttons';
import { TextInput } from '@cbhq/cds-web/controls';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalProps } from '@cbhq/cds-web/overlays';

import { useProject } from ':cloud/contexts/ProjectContext';
import { useGetProjects } from ':cloud/queries/Projects/useGetProjects';
import { useUpdateProject } from ':cloud/queries/Projects/useUpdateProject';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { generateProjectNameSchema } from ':cloud/utils/validate';

type ProjectEditModalProps = Pick<ModalProps, 'visible' | 'onRequestClose'> & {
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
};

export function ProjectEditModal({ visible, onRequestClose }: ProjectEditModalProps) {
  const { selectedProject } = useProject();
  const { activeOrg } = useGetUser();
  const projectId = selectedProject?.id || '';
  const projectName = selectedProject?.name || '';
  const organizationId = activeOrg?.organizationId || '';
  const { projects } = useGetProjects(organizationId);
  const updateProjectMutation = useUpdateProject();

  const [value, setValue] = useState(projectName || '');
  const [error, setError] = useState('');

  useEffect(() => {
    setValue(projectName || '');
  }, [projectName]);

  const validationSchema = useMemo(() => {
    const existingProjectNames = projects.map((p) => p.name);
    return generateProjectNameSchema({ value: projectName }, existingProjectNames).test(
      'unique-name',
      'Project name must be unique',
      (inputValue) => !existingProjectNames.includes(inputValue),
    );
  }, [projects, projectName]);

  const handleOnRequestClose = useCallback(() => {
    setError('');
    onRequestClose?.();
  }, [onRequestClose]);

  const handleSubmit = useCallback(() => {
    try {
      validationSchema.validateSync({ value: value.trim() });
      updateProjectMutation.mutate(
        { id: projectId, name: value.trim(), organizationId },
        {
          onSuccess: () => {
            handleOnRequestClose();
          },
        },
      );
    } catch (validationError) {
      if (validationError instanceof ValidationError) {
        setError(validationError.message);
      }
    }
  }, [
    value,
    projectId,
    organizationId,
    updateProjectMutation,
    handleOnRequestClose,
    validationSchema,
  ]);

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  return (
    <Modal visible={visible} onRequestClose={handleOnRequestClose}>
      <ModalHeader title="Edit project" />
      <ModalBody>
        <TextInput
          label="Project name"
          value={value}
          placeholder="Name your project"
          onChange={handleOnChange}
          helperText={error}
          variant={error ? 'negative' : undefined}
        />
      </ModalBody>
      <ModalFooter
        primaryAction={
          <Button
            variant="primary"
            onPress={handleSubmit}
            loading={updateProjectMutation.isLoading}
          >
            Save
          </Button>
        }
        secondaryAction={
          <Button variant="secondary" onPress={handleOnRequestClose}>
            Cancel
          </Button>
        }
      />
    </Modal>
  );
}
